/**
 * Which home page sections Design B draws for a tenant (1NOW-11111).
 *
 * The footer and header links an operator builds in the FMS point at home page sections
 * ("/#section-faqs"). Design B draws a section only when it has content, and has no
 * "section-about-us" or "section-all-fleet" at all, so a link to one of those went nowhere.
 * Design B's home page reads these same rules, so the page and the links cannot drift.
 */

// A section is shown unless the admin explicitly toggled it off in the FMS.
// `is_visible` is only ever `false` when hidden; null/undefined (older rows
// or sections that never had the field) read as visible.
const isShown = (section: any) => section?.is_visible !== false;
const hasKeypoints = (section: any) => section?.keypoints?.length > 0;

export const aboutShown = (s: any) => isShown(s) && !!(s?.heading || s?.description || s?.image_url);
export const testimonialsShown = (s: any) => isShown(s) && !!(s?.heading || hasKeypoints(s));
export const whyUsShown = (s: any) =>
  isShown(s) && !!(s?.heading || s?.description || s?.image_url || hasKeypoints(s));
export const faqsShown = (s: any) => isShown(s) && !!(s?.heading || s?.description || hasKeypoints(s));

/** The ids on Design B's home page, given the tenant's sections. "section" is always there. */
export const designBHomeIds = (d: { aboutUs?: any; testimonials?: any; whyUs?: any; faqs?: any }): string[] =>
  [
    "section",
    aboutShown(d.aboutUs) && "section-about",
    testimonialsShown(d.testimonials) && "section-testimonials",
    whyUsShown(d.whyUs) && "section-why-choose-us",
    faqsShown(d.faqs) && "section-faqs",
  ].filter((id): id is string => !!id);

/**
 * True for a link to a "#section…" of the home page that `ids` does not hold. A link to another
 * page, to another site, or to an id that is not a home section is none of this function's
 * business and is never reported missing.
 */
export const pointsAtMissingSection = (href: unknown, ids: string[]): boolean => {
  if (typeof href !== "string" || !href.includes("#section")) return false;
  try {
    const url = new URL(href.trim(), window.location.origin);
    if (url.origin !== window.location.origin || !["/", "/home"].includes(url.pathname)) return false;
    const id = decodeURIComponent(url.hash.slice(1));
    return id.startsWith("section") && !ids.includes(id);
  } catch {
    return false;
  }
};
